/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Immutable Credit Ledger Data Table Component (Task 10.6.3)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React, { useState } from 'react';
import {
  ListFilter,
  Search,
  Download,
  Copy,
  Check,
  Calendar,
  Layers,
  Eye,
  ArrowDownLeft,
  ArrowUpRight,
  RotateCcw,
  Clock,
} from 'lucide-react';
import { useTranslation } from '../../i18n';
import {
  useCreditLedgerStore,
  TypeFilter,
  DateRangeFilter,
  LedgerCategory,
  LedgerEntry,
} from '../../stores/useCreditLedgerStore';

export const CreditLedgerTable: React.FC = () => {
  const { t } = useTranslation();
  const {
    searchQuery,
    typeFilter,
    categoryFilter,
    dateRange,
    setSearchQuery,
    setTypeFilter,
    setCategoryFilter,
    setDateRange,
    getFilteredEntries,
    openDetailModal,
    exportLedgerAsCsv,
  } = useCreditLedgerStore();

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const entries = getFilteredEntries();

  const handleCopy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      // Fallback
    }
  };

  const handleDownloadCsv = () => {
    const csvContent = exportLedgerAsCsv();
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `sophia-credit-ledger-${Date.now()}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const formatTimestamp = (timestamp: number) => {
    const d = new Date(timestamp);
    const day = d.getDate().toString().padStart(2, '0');
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const hour = d.getHours().toString().padStart(2, '0');
    const min = d.getMinutes().toString().padStart(2, '0');
    return `${day}/${month} ${hour}:${min}`;
  };

  const formatRelativeTime = (timestamp: number) => {
    const diffSec = Math.floor((Date.now() - timestamp) / 1000);
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  };

  const renderTypeBadge = (entry: LedgerEntry) => {
    if (entry.type === 'TOPUP_PURCHASE' || entry.type === 'PLAN_ALLOCATION') {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 border border-emerald-500/20">
          <ArrowDownLeft className="size-3" />
          {entry.type === 'TOPUP_PURCHASE' ? 'Top-up' : 'Plan Grant'}
        </span>
      );
    }
    if (entry.type === 'REFUND') {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-500 border border-sky-500/20">
          <RotateCcw className="size-3" />
          Refund
        </span>
      );
    }
    if (entry.type === 'ADMIN_ADJUSTMENT') {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-accent/15 text-accent border border-accent/20">
          Admin Adj
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full bg-primary/10 text-primary border border-primary/20">
        <ArrowUpRight className="size-3" />
        AI Usage
      </span>
    );
  };

  return (
    <div className="p-5 rounded-2xl border border-surface-border bg-surface space-y-4">
      {/* Table Header and Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <ListFilter className="size-4 text-primary" />
            <h3 className="text-sm font-bold font-heading text-foreground">
              {t('ledger.title')}
            </h3>
            <span className="text-[10px] px-2 py-0.5 rounded-full font-bold bg-primary/10 text-primary">
              {entries.length} entries
            </span>
          </div>
          <p className="text-xs text-foreground-muted">
            {t('ledger.subtitle')}
          </p>
        </div>

        {/* Export CSV Button */}
        <button
          onClick={handleDownloadCsv}
          className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-surface-border bg-surface-elevated hover:bg-surface-subtle text-xs font-semibold text-foreground transition-all shadow-xs active:scale-95 shrink-0 self-start sm:self-auto"
        >
          <Download className="size-3.5 text-primary" />
          <span>{t('ledger.exportCsv')}</span>
        </button>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 pt-1">
        {/* Search Input */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-foreground-muted" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('ledger.searchPlaceholder')}
            className="w-full pl-9 pr-4 py-2 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground placeholder:text-foreground-muted focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all"
          />
        </div>

        {/* Filter Controls Group */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Category Dropdown */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground">
            <Layers className="size-3.5 text-foreground-muted" />
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value as 'all' | LedgerCategory)}
              className="bg-transparent border-none text-xs text-foreground focus:outline-none cursor-pointer"
            >
              <option value="all">{t('ledger.catAll')}</option>
              <option value="text">{t('ledger.catText')}</option>
              <option value="chat">{t('ledger.catChat')}</option>
              <option value="images">{t('ledger.catImages')}</option>
              <option value="video">{t('ledger.catVideo')}</option>
              <option value="avatar">{t('ledger.catAvatar')}</option>
              <option value="voice">{t('ledger.catVoice')}</option>
              <option value="translation">{t('ledger.catTranslation')}</option>
              <option value="billing">{t('ledger.catBilling')}</option>
            </select>
          </div>

          {/* Date Range Dropdown */}
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-surface-elevated border border-surface-border text-xs text-foreground">
            <Calendar className="size-3.5 text-foreground-muted" />
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as DateRangeFilter)}
              className="bg-transparent border-none text-xs text-foreground focus:outline-none cursor-pointer"
            >
              <option value="all">{t('ledger.dateAll')}</option>
              <option value="today">{t('ledger.dateToday')}</option>
              <option value="7d">{t('ledger.date7d')}</option>
              <option value="30d">{t('ledger.date30d')}</option>
              <option value="month">{t('ledger.dateMonth')}</option>
            </select>
          </div>

          {/* Type Filter Pills */}
          <div className="p-0.5 rounded-xl bg-surface-elevated border border-surface-border flex items-center gap-0.5">
            {(
              [
                { id: 'all', label: t('ledger.typeAll') },
                { id: 'debit', label: t('ledger.typeDebits') },
                { id: 'credit', label: t('ledger.typeCredits') },
                { id: 'refund', label: t('ledger.typeRefunds') },
              ] as const
            ).map((tf) => (
              <button
                key={tf.id}
                onClick={() => setTypeFilter(tf.id as TypeFilter)}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                  typeFilter === tf.id
                    ? 'bg-primary text-primary-fg font-bold shadow-xs'
                    : 'text-foreground-muted hover:text-foreground'
                }`}
              >
                {tf.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Table Display */}
      <div className="overflow-x-auto rounded-xl border border-surface-border">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="border-b border-surface-border bg-surface-subtle text-[11px] font-semibold text-foreground-muted uppercase tracking-wider">
              <th className="py-3 px-4">{t('ledger.colTimestamp')}</th>
              <th className="py-3 px-3">{t('ledger.colTxId')}</th>
              <th className="py-3 px-4">{t('ledger.colEvent')}</th>
              <th className="py-3 px-4">{t('ledger.colUnits')}</th>
              <th className="py-3 px-3 text-right">{t('ledger.colAmount')}</th>
              <th className="py-3 px-4 text-right">{t('ledger.colBalanceAfter')}</th>
              <th className="py-3 px-3 text-center">{t('ledger.colActions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {entries.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-12 text-center text-foreground-muted space-y-1">
                  <p className="font-bold text-foreground text-sm">{t('ledger.emptyTitle')}</p>
                  <p className="text-xs">{t('ledger.emptyDesc')}</p>
                </td>
              </tr>
            ) : (
              entries.map((entry) => {
                const isCredit = entry.amount > 0;
                const isDebit = entry.amount < 0;

                return (
                  <tr
                    key={entry.id}
                    className="hover:bg-surface-subtle/50 transition-colors group"
                  >
                    {/* Timestamp */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <Clock className="size-3 text-foreground-muted shrink-0" />
                        <span className="font-mono text-foreground font-medium">
                          {formatTimestamp(entry.timestamp)}
                        </span>
                        <span className="text-[10px] text-foreground-muted">
                          ({formatRelativeTime(entry.timestamp)})
                        </span>
                      </div>
                    </td>

                    {/* Transaction ID */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      <button
                        onClick={() => handleCopy(entry.id, entry.id)}
                        className="inline-flex items-center gap-1 font-mono text-[11px] text-foreground hover:text-primary transition-colors"
                        title="Copy Transaction ID"
                      >
                        <span>{entry.id}</span>
                        {copiedId === entry.id ? (
                          <Check className="size-3 text-emerald-500 ml-0.5" />
                        ) : (
                          <Copy className="size-3 text-foreground-muted opacity-0 group-hover:opacity-100 transition-opacity ml-0.5" />
                        )}
                      </button>
                    </td>

                    {/* Event & Model */}
                    <td className="py-3 px-4">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          {renderTypeBadge(entry)}
                          {entry.modelCode && (
                            <code className="text-[10px] font-mono px-1.5 py-0.2 rounded bg-surface-elevated border border-surface-border text-foreground-muted">
                              {entry.modelCode}
                            </code>
                          )}
                        </div>
                        <p className="text-[11px] text-foreground-muted line-clamp-1 max-w-xs">
                          {entry.description}
                        </p>
                      </div>
                    </td>

                    {/* Units Used */}
                    <td className="py-3 px-4 whitespace-nowrap">
                      <span className="text-xs text-foreground font-medium">
                        {entry.unitsUsed || '—'}
                      </span>
                    </td>

                    {/* Credit Amount Delta */}
                    <td className="py-3 px-3 whitespace-nowrap text-right">
                      <span
                        className={`font-mono font-bold text-xs ${
                          isCredit
                            ? 'text-emerald-500'
                            : isDebit
                            ? 'text-rose-500'
                            : 'text-foreground'
                        }`}
                      >
                        {isCredit ? `+${entry.amount}` : entry.amount} CR
                      </span>
                    </td>

                    {/* Balance After */}
                    <td className="py-3 px-4 whitespace-nowrap text-right">
                      <span className="font-mono font-semibold text-foreground text-xs">
                        {entry.balanceAfter.toLocaleString()} CR
                      </span>
                    </td>

                    {/* Action: View Detail */}
                    <td className="py-3 px-3 whitespace-nowrap text-center">
                      <button
                        onClick={() => openDetailModal(entry)}
                        className="p-1.5 rounded-lg text-foreground-muted hover:text-primary hover:bg-surface-elevated transition-colors inline-flex items-center justify-center"
                        title={t('ledger.colActions')}
                      >
                        <Eye className="size-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
